import {
  browserLocalPersistence,
  getAdditionalUserInfo,
  getAuth,
  OAuthProvider,
  setPersistence,
  signInWithPopup,
  signOut,
  UserCredential,
  signInWithRedirect,
  getRedirectResult,
  User,
} from 'firebase/auth';
import {Timestamp} from 'firebase/firestore';
import {httpsCallable} from 'firebase/functions';
import {
  createInitialSystemMessage,
  Keys,
  ScalekitUser,
  sanitizeString,
  Interest,
  TranslatedLanguage,
  Department,
} from 'hubbl-shared';
import {Api} from '../../api';
import {firebaseFunctions} from '../../api/firebase';
import {CreateEventFormType} from '../../models';
import {EventDTO} from '../../models/event/EventDTO';
import {getAuthUser, getAuthUserCompanyId} from '../../utils';
import {createAppAsyncThunk} from '../appAsyncThunk';
import {UserSelector} from './UserSelector';
import {storage} from '../../api/storage/ApiStorage';

/**
 * Helper to check if a Firebase user has a specific provider linked.
 * Handles multi-provider accounts where providerData contains multiple entries.
 */
export const userHasProvider = (
  user: User | null,
  providerId: string,
): boolean => {
  if (!user?.providerData) return false;
  return user.providerData.some(provider => provider.providerId === providerId);
};

export const createEvent = createAppAsyncThunk(
  'user/createEvent',
  async (
    {
      coverImage,
      eventDescription,
      eventEmoji,
      eventLocation,
      eventTitle,
      eventTags,
      marketingStartDate,
      endDateTime,
      deadline,
      reminders,
      startDateTime,
      adminInvitedEmployees,
      evaluationReminders,
      isEvaluationEnabled,
      evaluationEndDate,
      evaluationCustomDescription,
      ticketSystemEnabled,
    }: CreateEventFormType,
    thunkApi,
  ) => {
    try {
      const userCompanyId = await getAuthUserCompanyId();

      const userDetails = await Api.user.getUserDetails(
        userCompanyId,
        getAuthUser()?.uid ?? '',
      );
      const now = new Date();

      const eventId = `${sanitizeString(eventTitle)}_${now.getTime()}`;

      const imageRef = await Api.storage.uploadImage(
        coverImage,
        userCompanyId,
        eventId,
      );

      if (!userDetails) {
        throw Error('No user details');
      }

      const questions = UserSelector.getEventCreationQuestions(
        thunkApi.getState(),
      );

      const evaluationQuestions =
        UserSelector.getEventCreationEvaluationQuestions(thunkApi.getState());

      const visibilityFilters = UserSelector.getEventCreationVisibilityFilters(
        thunkApi.getState(),
      );

      const newEventItem: EventDTO = {
        attendees: [],
        attendeeIds: [],
        ticketSystem: {
          checkIns: {},
          isEnabled: ticketSystemEnabled,
        },
        maxAttendees: null,
        waitlist: [],
        companyId: userCompanyId,
        adminInvitedEmployees: adminInvitedEmployees,
        coverImageUrl: imageRef,
        created: Timestamp.fromDate(now),
        creatorId: userDetails.id,
        tasks: [],
        deadline: deadline ? Timestamp.fromDate(new Date(deadline)) : null,
        declinedUserIds: [],
        declinedUsers: [],
        endDateTime: endDateTime
          ? Timestamp.fromDate(new Date(endDateTime))
          : null,
        eventDescription: {
          da: eventDescription,
          en: eventDescription,
        },
        reminders,
        eventEmoji,
        eventLocation,
        eventTags: eventTags || [],
        eventTitle: {
          da: eventTitle,
          en: eventTitle,
        },
        questionResponses: {},
        filters: visibilityFilters,
        id: eventId,
        isRepeatedHubbl: false,
        isRepeatEnabled: false,
        marketingStartDate: Timestamp.fromDate(new Date(marketingStartDate)),
        messages: [createInitialSystemMessage()],
        officeId: Keys.defaultOffice,
        startDateTime: Timestamp.fromDate(new Date(startDateTime)),
        invitedUsers: [],
        questions,
        evaluation: isEvaluationEnabled
          ? {
              customDescription: evaluationCustomDescription,
              questions: evaluationQuestions,
              responses: {},
              reminders: evaluationReminders,
              endDate: Timestamp.fromDate(new Date(evaluationEndDate ?? '')),
            }
          : null,
      };

      await Api.event.addEvent(userCompanyId, newEventItem);

      return newEventItem;
    } catch (error) {
      throw Error(`Error happened: ${JSON.stringify(error)}`);
    }
  },
);
export const fetchUserDetails = createAppAsyncThunk(
  'user/fetchUserDetails',
  async () => {
    const userCompanyId = await getAuthUserCompanyId();

    const userDetails = await Api.user.getUserDetails(
      userCompanyId,
      getAuthUser()?.uid ?? '',
    );

    return userDetails;
  },
);
interface SignInUserProps {
  email: string;
  password: string;
}

interface SignUpUserArgs {
  email: string;
  password: string;
  companyPincode?: string; // Now optional - undefined when creating new company
  companyName?: string; // Now optional - undefined when creating new company
}

interface SignUpUserSuccessPayload {
  email: string;
  companyName: string;
}

interface SignUpUserErrorPayload {
  code: string;
  message: string;
}

const parseFirebaseAuthError = (error: unknown): SignUpUserErrorPayload => {
  if (
    error &&
    typeof error === 'object' &&
    'code' in error &&
    typeof (error as {code: unknown}).code === 'string'
  ) {
    const firebaseCode = (error as {code: string}).code;
    const firebaseMessage =
      'message' in error &&
      typeof (error as {message: unknown}).message === 'string'
        ? (error as {message: string}).message
        : firebaseCode;
    return {code: firebaseCode, message: firebaseMessage};
  }

  return {
    code: 'auth/unknown',
    message:
      error instanceof Error ? error.message : 'Unknown authentication error',
  };
};

export const signInAdmin = createAppAsyncThunk(
  'user/signInAdmin',
  async ({email, password}: SignInUserProps, _thunkApi) => {
    try {
      const auth = getAuth();
      await setPersistence(auth, browserLocalPersistence);

      const userCredentials = await Api.user.onLogIn(email, password);

      // Call Firebase Function to check if user is an admin

      const checkStatus = httpsCallable<{isAdmin: boolean}>(
        firebaseFunctions,
        'onCheckAuth',
      );

      const response = await checkStatus();

      //@ts-ignore
      const isAdmin = response.data.isAdmin as Boolean;

      if (!isAdmin) {
        await signOut(auth);
        throw new Error('Access denied. Admins only.');
      }

      return userCredentials;
    } catch (error) {
      console.error('Error signing in:', error);
      throw Error('Error with sign in');
    }
  },
);

export const signInUser = createAppAsyncThunk(
  'user/signInUser',
  async ({email, password}: SignInUserProps, _thunkApi) => {
    try {
      const auth = getAuth();
      await setPersistence(auth, browserLocalPersistence);

      const userCredentials = await Api.user.onLogIn(email, password);

      return userCredentials;
    } catch (error) {
      console.error('Error signing in:', error);
      throw Error('Error with sign in');
    }
  },
);

export const signUpUser = createAppAsyncThunk<
  SignUpUserSuccessPayload,
  SignUpUserArgs,
  {rejectValue: SignUpUserErrorPayload}
>(
  'user/signUpUser',
  async ({email, password, companyPincode, companyName}, thunkApi) => {
    try {
      const userCredential = await Api.user.signUpWithEmail(email, password);
      await Api.user.sendEmailVerification(userCredential.user);

      // Only move to company if pincode is provided (existing company flow)
      if (companyPincode) {
        setTimeout(() => {
          Api.user.onMoveUnattachedUserToCompany(companyPincode);
        }, 1000);
      }
      // If no pincode: user stays in UNKNOWN_COMPANY for get-started flow

      return {email, companyName: companyName || ''};
    } catch (error: unknown) {
      const parsedError = parseFirebaseAuthError(error);
      return thunkApi.rejectWithValue(parsedError);
    }
  },
);

export const signOutUser = createAppAsyncThunk('user/signOutUser', async () => {
  await signOut(getAuth());
});

interface SignInEnterpriseSSOProps {
  email: string;
}

export const signInEnterpriseSSO = createAppAsyncThunk(
  'user/signInEnterpriseSSO',
  async ({email}: SignInEnterpriseSSOProps, _thunkApi) => {
    try {
      const persistenceMode = browserLocalPersistence;
      const auth = getAuth();
      await setPersistence(auth, persistenceMode);

      const {
        data: {hasConnection},
      } = await Api.user.onCheckScalekitConnection(email);

      if (!hasConnection) {
        throw new Error('Enterprise SSO not available for this email');
      }

      const provider = new OAuthProvider('oidc.scalekit');
      provider.setCustomParameters({
        domain: email.split('@')[1] || '',
        login_hint: email,
      });

      try {
        // First attempt with popup
        const result = await signInWithPopup(auth, provider);
        return result;
      } catch (error: any) {
        // Check if error is due to popup blocking
        if (
          error?.code?.includes('auth/popup-blocked') ||
          error?.code?.includes('auth/popup-closed-by-user')
        ) {
          // Fallback to redirect
          await signInWithRedirect(auth, provider);
          // Note: The redirect will cause a page reload, so we won't reach this point
          throw new Error('Redirecting to SSO provider...');
        }
        throw error;
      }
    } catch (error) {
      console.error('Error with enterprise SSO sign in:', error);
      throw Error('Error with enterprise SSO sign in');
    }
  },
);

export const handleEnterpriseSSORedirect = createAppAsyncThunk(
  'user/handleEnterpriseSSORedirect',
  async (result: UserCredential | null, _thunkApi) => {
    try {
      // Check if user has OIDC provider (handles multi-provider accounts)
      if (!result?.user || !userHasProvider(result.user, 'oidc.scalekit')) {
        throw new Error('Invalid SSO redirect result');
      }

      // Get the raw user info from the OAuth provider
      const additionalUserInfo = getAdditionalUserInfo(result);

      if (!additionalUserInfo) {
        throw new Error('No additional user info found');
      }
      const scalekitUser = additionalUserInfo
        ? (additionalUserInfo?.profile as ScalekitUser)
        : undefined;
      if (!scalekitUser) {
        throw new Error('No Scalekit user found');
      }

      const isNewUser = additionalUserInfo.isNewUser;

      if (isNewUser) {
        try {
          await Api.user.onSyncAndMoveOIDCAuthUser({
            photoURL: result.user.photoURL
              ? result.user.photoURL
              : (scalekitUser.picture ?? ''),
            firstName: result.user.displayName?.split(' ')[0] || '',
            lastName:
              result.user.displayName?.split(' ').slice(1).join(' ') || '',
            providerUserId: scalekitUser.sub,
            customProperties: {
              department: scalekitUser.department,
              companyname: scalekitUser.companyname,
              employeehiredate: scalekitUser.employeehiredate,
              officelocation: scalekitUser.officelocation,
              jobtitle: scalekitUser.jobtitle,
            },
          });
          //Wait for the user to be synced
          await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (error) {
          console.error('Error syncing and moving OIDC auth user:', error);
          throw Error('Error syncing and moving OIDC auth user');
        }
      }

      await result.user.reload();

      const companyId = await getAuthUserCompanyId();

      if (!companyId) {
        throw new Error('User does not belong to an org');
      }

      // For admin access check
      if (!result.user.email) {
        throw new Error('No email found in SSO result');
      }

      let isAdmin = false;

      try {
        // Check if user is admin
        const checkStatus = httpsCallable<{isAdmin: boolean}>(
          firebaseFunctions,
          'onCheckAuth',
        );
        const response = await checkStatus();
        isAdmin = (response.data as {isAdmin: boolean}).isAdmin;
      } catch (error) {
        console.error('Error checking admin status:', error);
        isAdmin = false;
      }

      return {isAdmin, user: result.user, additionalUserInfo};
    } catch (error) {
      //@ts-ignore
      throw Error(error?.message ?? '');
    }
  },
);

export const handleRedirectResult = createAppAsyncThunk(
  'user/handleRedirectResult',
  async (_, _thunkApi) => {
    try {
      const auth = getAuth();
      const result = await getRedirectResult(auth);

      if (!result) {
        return null;
      }

      return result;
    } catch (error) {
      console.error('Error handling redirect result:', error);
      throw Error('Error handling redirect result');
    }
  },
);

export const updateUserInterests = createAppAsyncThunk(
  'user/updateUserInterests',
  async (interests: Interest[], _thunkApi) => {
    const companyId = await getAuthUserCompanyId();

    const user = getAuthUser();

    if (!companyId || !user) throw new Error('No user or company');
    await Api.user.updateUserDetails(companyId, user.uid, {interests});
    return interests;
  },
);

export const updateUserBasicInfo = createAppAsyncThunk(
  'user/updateUserBasicInfo',
  async (
    data: {fullName: string; jobTitle: string; department: Department},
    _thunkApi,
  ) => {
    const companyId = await getAuthUserCompanyId();
    const user = getAuthUser();

    if (!companyId || !user) throw new Error('No user or company');

    const firstName = data.fullName.split(' ')[0];

    await Api.user.updateUserDetails(companyId, user.uid, {
      fullName: data.fullName,
      firstName,
      jobTitle: data.jobTitle,
      department: data.department,
    });

    return {...data, firstName};
  },
);

export const updateUserProfilePhoto = createAppAsyncThunk(
  'user/updateUserProfilePhoto',
  async (file: File, _thunkApi) => {
    const companyId = await getAuthUserCompanyId();
    const user = getAuthUser();
    if (!companyId || !user) throw new Error('No user or company');
    // Upload image to storage
    const storagePath = await storage.uploadProfilePicture(
      file,
      companyId,
      user.uid,
    );

    // Update user profile with photoUrl
    await Api.user.updateUserDetails(companyId, user.uid, {
      photoUrl: storagePath,
    });
    return storagePath;
  },
);

export const completeCreateProfileFlow = createAppAsyncThunk(
  'user/completeCreateProfileFlow',
  async (_, _thunkApi) => {
    const companyId = await getAuthUserCompanyId();
    const user = getAuthUser();
    if (!companyId || !user) throw new Error('No user or company');
    await Api.user.completeCreateProfileFlow(companyId, user.uid, {
      isOnboarded: true,
    });
    return {};
  },
);

export const createCompanyForCurrentUser = createAppAsyncThunk(
  'user/createCompanyForCurrentUser',
  async (
    {companyName, fullName}: {companyName: string; fullName: string},
    _thunkApi,
  ) => {
    const companyId = await getAuthUserCompanyId();
    const user = getAuthUser();

    if (!user) throw new Error('No authenticated user');

    // Allow undefined/empty companyId (no claim set yet) or UNKNOWN_COMPANY
    if (companyId && companyId !== 'UNKNOWN_COMPANY') {
      throw new Error('User already has a company');
    }

    // Update user's full name first
    await Api.user.updateUserDetails('UNKNOWN_COMPANY', user.uid, {
      fullName,
      firstName: fullName.split(' ')[0],
    });

    // Create company and move user
    const result = await Api.company.createCompanyForAuthenticatedUser({
      companyName,
    });

    // Force token refresh to get new claims
    await user.getIdToken(true);

    return result;
  },
);

export const updateUserLanguage = createAppAsyncThunk(
  'user/updateUserLanguage',
  async (language: TranslatedLanguage, thunkApi) => {
    const companyId = await getAuthUserCompanyId();
    const user = getAuthUser();
    if (!companyId || !user) throw new Error('No user or company');

    // Get current user settings to preserve other settings

    const currentUser = UserSelector.getUserDetails(thunkApi.getState());
    if (!currentUser) throw new Error('User not found');

    await Api.user.updateUserDetails(companyId, user.uid, {
      settings: {
        ...currentUser.settings,
        preferredLanguage: language,
      },
    });

    // Set moment locale
    const {setMomentLocale} = await import('../../utils/momentLocale');
    setMomentLocale(language);

    return language;
  },
);

export const initializeUserLanguage = createAppAsyncThunk(
  'user/initializeUserLanguage',
  async (userDetails: any, {dispatch}) => {
    const language =
      userDetails?.settings?.preferredLanguage || TranslatedLanguage.English;

    if (userDetails?.settings?.preferredLanguage) {
      // Import the global action dynamically to avoid circular dependency
      const {setPreferredLanguage} = await import('../global/GlobalReducer');
      dispatch(setPreferredLanguage(userDetails.settings.preferredLanguage));
    }

    // Set moment locale
    const {setMomentLocale} = await import('../../utils/momentLocale');
    setMomentLocale(language);

    return language;
  },
);
